import { useState, useCallback } from 'react';
import { useToast } from '@/contexts/admin/ToastContext';
import { useAppDispatch } from '@/store';
import { setPayingWithSourceId } from '@/store/slices/residents/paymentsSlice';

interface FormData {
  [key: string]: any;
}

interface FormErrors {
  [key: string]: string | undefined;
}

interface UsePaymentFormOptions {
  initialData?: FormData;
  onSuccess?: () => void;
  successMessage?: string;
}

export const usePaymentForm = <T extends FormData>(
  options: UsePaymentFormOptions = {}
) => {
  const { initialData = {}, onSuccess, successMessage } = options;
  const { showSuccess, showError } = useToast();
  const dispatch = useAppDispatch();
  
  const [formData, setFormData] = useState<T>(initialData as T);
  const [errors, setErrors] = useState<FormErrors>({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  const updateField = useCallback((field: keyof T, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    
    // Clear error for this field when user starts typing
    if (errors[field as string]) {
      setErrors(prev => ({ ...prev, [field]: undefined }));
    }
  }, [errors]);

  const setFieldError = useCallback((field: keyof T, error: string) => {
    setErrors(prev => ({ ...prev, [field]: error }));
  }, []);

  const clearErrors = useCallback(() => {
    setErrors({});
  }, []);

  const handleSubmitSuccess = useCallback((sourceId?: number) => {
    if (sourceId) {
      dispatch(setPayingWithSourceId(sourceId));
    }
    if (successMessage) {
      showSuccess(successMessage);
    }
    if (onSuccess) {
      onSuccess();
    }
  }, [dispatch, showSuccess, successMessage, onSuccess]);

  const handleSubmitError = useCallback((error: any, message?: string) => {
    console.error('Form submission error:', error);
    showError(message || 'An error occurred. Please try again.');
  }, [showError]);

  const submitForm = useCallback(async (
    submitFn: (data: T) => Promise<{ success: boolean; response?: any }>,
    validate?: (data: T) => FormErrors
  ) => {
    if (validate) {
      const validationErrors = validate(formData);
      if (Object.keys(validationErrors).length > 0) {
        setErrors(validationErrors);
        return false;
      }
    }

    setIsSubmitting(true);
    try {
      const result = await submitFn(formData);
      if (result.success) {
        const sourceId = result.response?.dataValues?.sourceId;
        handleSubmitSuccess(sourceId ? Number(sourceId) : undefined);
        return true;
      } else {
        handleSubmitError(null, 'Operation failed. Please try again.');
        return false;
      }
    } catch (error) {
      handleSubmitError(error);
      return false;
    } finally {
      setIsSubmitting(false);
    }
  }, [formData, handleSubmitSuccess, handleSubmitError]);

  return {
    formData,
    errors,
    isSubmitting,
    updateField,
    setFieldError,
    clearErrors,
    submitForm,
    handleSubmitSuccess,
    handleSubmitError,
  };
};

export default usePaymentForm;